// GENERATED by helm-console/scripts/ensure-seismo.mjs — DO NOT EDIT.
// Source of truth: helm-bridge/src/client/seismo.ts
// Edit that file; this copy is regenerated on every console build.
// Fleet dashboard — the "seismograph": a hand-rolled inline-SVG amplitude
// waveform of a time series over the last N minutes. No charting library (none
// in the repo); this is the dag.ts svgEl idiom emitted as a string so it can be
// dropped straight into the dashboard's innerHTML render and redrawn on each
// metrics tick. Auto-scales Y to the series' own peak — so a feature waveform
// and the epic sum that contains it read at DIFFERENT scales, by design.

export interface SeriesPoint {
  t: number; // sample wall-time (ms)
  v: number; // value (throughput tokens / pane count / commits)
}

export interface SeismoOpts {
  width?: number;
  height?: number;
  windowMs: number;
  now: number;
  /** Fix the Y scale (e.g. share one scale across sibling rows). Default:
   * auto-scale to the series' own peak. */
  peak?: number;
  /** 'area' (filled envelope, default) or 'bars' (discrete columns). */
  kind?: 'area' | 'bars';
  /** extra class on the <svg> for level-specific coloring */
  cls?: string;
  /** The caller's semantic series id, stamped as `data-mark-channel`. The
   * waveform's six call sites already name their series in `cls` as free text
   * (`lvl-feat` ×2, `lvl-epic`, `lvl-pane`, `lvl-commit`, `lvl-proj`) — and
   * three of those six carry NO CSS rule at all, so the class is not a
   * reliable channel. The channel is the one that is read by measurement;
   * `cls` stays until the waveform unification retires it. */
  channel?: string;
}

/** Render a waveform SVG string. Empty/all-zero series → a faint flatline (a
 * dead/idle scope reads as a flat baseline, which is the intended signal). */
export function seismoSvg(points: SeriesPoint[], opts: SeismoOpts): string {
  const W = opts.width ?? 130;
  const H = opts.height ?? 26;
  const { windowMs, now } = opts;
  const cls = opts.cls ? ` ${opts.cls}` : '';
  const inWindow = points.filter((p) => now - p.t <= windowMs && now - p.t >= 0);
  const peak = opts.peak ?? Math.max(1, ...inWindow.map((p) => p.v));

  const x = (t: number): number => {
    const frac = 1 - (now - t) / windowMs; // 0 = left edge (oldest), 1 = right (now)
    return Math.max(0, Math.min(1, frac)) * W;
  };
  const y = (v: number): number => H - Math.max(0, Math.min(1, v / peak)) * (H - 1);

  // The mark stamp. `data-mark` names the family, `data-mark-set` names the
  // mark set that drew it — the literal "blueprint" everywhere until the mark
  // registry lands, which is the MEASUREMENT (a non-Blueprint style still
  // stamping "blueprint" is a fallback the parity probe can see), not a
  // placeholder. Attributes only: this file is copied VERBATIM into the
  // console bundle by helm-console/scripts/ensure-seismo.mjs and must stay
  // import-free.
  // Slug-only: a channel is a series id, never free text, so it is filtered
  // rather than escaped (this file may not import an escaper).
  const ch = (opts.channel ?? '').replace(/[^a-zA-Z0-9_-]/g, '');
  const chan = ch ? ` data-mark-channel="${ch}"` : '';
  const open =
    `<svg class="seismo-svg${cls}" data-mark="wave" data-mark-set="blueprint"${chan}` +
    ` viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" preserveAspectRatio="none" aria-hidden="true">`;
  const baseline = `<line class="seismo-base" x1="0" y1="${H - 0.5}" x2="${W}" y2="${H - 0.5}"/>`;

  if (inWindow.length === 0 || peak <= 0) {
    return `${open}${baseline}</svg>`;
  }

  const pts = inWindow.slice().sort((a, b) => a.t - b.t);

  if (opts.kind === 'bars') {
    const bw = Math.max(1, W / Math.max(24, pts.length));
    const bars = pts
      .map((p) => {
        const bx = x(p.t);
        const by = y(p.v);
        return `<rect class="seismo-bar" x="${(bx - bw / 2).toFixed(1)}" y="${by.toFixed(1)}" width="${bw.toFixed(
          1,
        )}" height="${(H - by).toFixed(1)}"/>`;
      })
      .join('');
    return `${open}${baseline}${bars}</svg>`;
  }

  // area: line across the samples, closed down to the baseline for the fill
  const line = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(p.t).toFixed(1)},${y(p.v).toFixed(1)}`).join(' ');
  const areaPath = `${line} L${x(pts[pts.length - 1]!.t).toFixed(1)},${H} L${x(pts[0]!.t).toFixed(1)},${H} Z`;
  return `${open}${baseline}<path class="seismo-area" d="${areaPath}"/><path class="seismo-line" d="${line}"/></svg>`;
}
